import { DownloadOutlined } from '@ant-design/icons';
import { message, Space } from 'antd';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Alert,
  Button,
  DatePicker,
  SearchInput,
} from '../../../../components/index';
import { useChangePagination } from '../../../../hooks';
import {
  CargoSuppressionReportItemType,
  useExportCargoSuppressionXmlMutation,
  useGetCargoSuppressionReportQuery,
} from '../../../../services/cargoSuppressionApi';
import { useLazyExportReportsQuery } from '../../../../services/exportApi';
import { createDateString } from '../../../../utils/utils';
import { SectionHeader } from '../styles';
import { SuppressionTable } from './suppressionTable/SuppressionTable';
import { SelectionKey } from './suppressionTable/columns';

export type SuppressionFiltersType = {
  code_or_vessel__name?: string;
  initial_date_report?: string | null;
  final_date_report?: string | null;
  page?: number;
};

export function SuppressionSection() {
  const { onChangePagination, queryPage } = useChangePagination();

  const [filters, setFilters] = useState<SuppressionFiltersType>({
    page: queryPage,
  });

  useEffect(() => {
    setFilters((prev) => ({ ...prev, page: queryPage }));
  }, [queryPage]);

  const { data, isFetching, refetch } =
    useGetCargoSuppressionReportQuery(filters);

  const [selected, setSelected] = useState<SelectionKey[]>([]);
  const [checkedAll, setCheckedAll] = useState(false);
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (pollingTimerRef.current) clearTimeout(pollingTimerRef.current);
    };
  }, []);

  const scheduleXmlStatusRefetch = useCallback(() => {
    const delays = [5000, 10000, 15000];
    let attempt = 0;
    const poll = () => {
      if (attempt >= delays.length) return;
      pollingTimerRef.current = setTimeout(() => {
        refetch();
        attempt++;
        poll();
      }, delays[attempt]);
    };
    poll();
  }, [refetch]);

  const [exportXml, { isLoading: isLoadingExportXml }] =
    useExportCargoSuppressionXmlMutation();
  const [exportReport, { isLoading: isExporting }] =
    useLazyExportReportsQuery();

  function isAllSelectedOnPage() {
    if (!data || !data.results || data.results.length === 0) return false;
    return data.results.every((item) =>
      selected.some(
        (s) =>
          s.docking_code === item.docking_code &&
          s.docking_year === item.docking_year
      )
    );
  }

  useEffect(() => {
    if (!isFetching) {
      setCheckedAll(isAllSelectedOnPage());
    }
  }, [data, isFetching]);

  function onSearch(value: string) {
    if (value !== '') {
      setFilters({ code_or_vessel__name: value });
    } else {
      setFilters((prev) => ({ ...prev, code_or_vessel__name: '' }));
    }
  }

  function onChangeDatePicker(dates: any) {
    if (dates === null) {
      setFilters((prev) => ({
        code_or_vessel__name: prev.code_or_vessel__name,
      }));
      return;
    }
    let filteredDates = {};
    if (dates && dates.length > 0 && dates[0]) {
      filteredDates = {
        ...filteredDates,
        initial_date_report: createDateString(dates[0], true),
      };
    }
    if (dates && dates.length > 1 && dates[1]) {
      filteredDates = {
        ...filteredDates,
        final_date_report: createDateString(dates[1], true),
      };
    }
    setFilters((prev) => ({ ...prev, ...filteredDates }));
  }

  function handleSelectItem(
    checked: boolean,
    record: CargoSuppressionReportItemType
  ) {
    if (checked) {
      setSelected((prev) => [
        ...prev,
        {
          docking_code: record.docking_code,
          docking_year: record.docking_year,
        },
      ]);
    } else {
      setSelected((prev) =>
        prev.filter(
          (s) =>
            !(
              s.docking_code === record.docking_code &&
              s.docking_year === record.docking_year
            )
        )
      );
      setCheckedAll(false);
    }
  }

  function handleCheckedAll(checked: boolean) {
    if (!data?.results) return;
    if (checked) {
      setCheckedAll(true);
      setSelected((prev) => {
        const existing = new Set(
          prev.map((s) => `${s.docking_code}-${s.docking_year}`)
        );
        const additions = data.results
          .filter(
            (item) => !existing.has(`${item.docking_code}-${item.docking_year}`)
          )
          .map((item) => ({
            docking_code: item.docking_code,
            docking_year: item.docking_year,
          }));
        return [...prev, ...additions];
      });
    } else {
      setCheckedAll(false);
      const pageKeys = new Set(
        data.results.map((i) => `${i.docking_code}-${i.docking_year}`)
      );
      setSelected((prev) =>
        prev.filter((s) => !pageKeys.has(`${s.docking_code}-${s.docking_year}`))
      );
    }
  }

  async function handleGenerateXml() {
    if (selected.length === 0) return;
    try {
      await exportXml({ list_of_atracacoes: selected }).unwrap();
      message.success(
        'XMLs em processamento. Você os receberá em seu e-mail em breve.',
        5
      );
      setSelected([]);
      setCheckedAll(false);
      scheduleXmlStatusRefetch();
    } catch (error) {
      message.error('Erro ao gerar XML. Tente novamente.');
    }
  }

  async function handleExport() {
    try {
      await exportReport({
        exportType: 'cargo-suppressions',
        params: {
          search: filters.code_or_vessel__name,
          initial_date: filters.initial_date_report || undefined,
          final_date: filters.final_date_report || undefined,
        },
      }).unwrap();
      message.success(
        'Exportação em processamento. Você a receberá em seu e-mail em breve.',
        5
      );
    } catch (error) {
      message.error('Erro ao solicitar a exportação. Tente novamente.');
    }
  }

  return (
    <>
      <SectionHeader style={{ flexDirection: 'column', gap: '15px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              marginInlineEnd: '10px',
            }}
          >
            <SearchInput
              size="large"
              allowClear
              placeholder="Pesquisar embarcação/atracação"
              style={{ width: '400px' }}
              onChange={debounce((evt) => onSearch(evt.target.value), 500)}
            />
            <div>
              <strong>{selected.length} </strong>
              {selected.length === 1 ? (
                <span>atracação selecionada</span>
              ) : (
                <span>atracações selecionadas</span>
              )}
            </div>
            <Alert
              type="warning"
              message="Selecione as atracações desejadas para ativar a geração do XML.
              Cada atracação selecionada gera um XML próprio, todos entregues no mesmo e-mail."
            />
          </div>
          <Space size={18}>
            <Button
              icon={<DownloadOutlined />}
              size="large"
              onClick={handleExport}
              loading={isExporting}
            >
              Exportar XLS
            </Button>
            <Button
              type="primary"
              onClick={handleGenerateXml}
              disabled={selected.length === 0}
              loading={isLoadingExportXml}
            >
              Gerar XML
            </Button>
          </Space>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginRight: 'auto',
            marginBottom: '20px',
          }}
        >
          <span style={{ width: '240px' }}>
            Filtro por período de desatracação:{'   '}
          </span>
          <DatePicker
            allowClear
            onChange={onChangeDatePicker}
            minuteStep={1}
            isRangePicker
            showTime={false}
            format={['DD/MM/YYYY']}
            style={{ width: '240px' }}
          />
        </div>
      </SectionHeader>
      <SuppressionTable
        dataSource={data?.results || []}
        onChangePagination={onChangePagination}
        total={data?.count}
        isLoading={isFetching}
        queryPage={queryPage}
        showSizeChanger={false}
        onSelectItem={handleSelectItem}
        selected={selected}
        checkedAll={checkedAll}
        onCheckedAll={handleCheckedAll}
        itemsPerPage={data?.results?.length}
      />
    </>
  );
}
